import { emptyApi } from "./emptyApi";
import qs from "qs";
import * as FILE_TYPE from "constants/fileTypes";
import {
  CompanyGroupResponse,
  CouponResponse,
  SliderCRM,
} from "types/Models/salescrm";
import { makeFormData } from "utils/makeFormData";
import { GroupInvitationRequestType } from "types/Models/groups";

type GetAllSalesGroupsProps = {
  pageNumber?: number | number;
  searchTerm?: string | null;
  viewFilter: string | null;
  archived?: boolean;
  subscriptionFilter?: string | null;
  agentFilter?: string | null;
  salespersonFilter?: string | null;
  usStateFilter?: string | null;
  incorporationMonthsFilter?: string | null;
  selectedTag?: string | null;
  sortDueBy?: string | null;
  taskState?: string | null;
  notLaunchedTaskFilter?: string | null;
  subscriberTypeFilter?: string | null;
  base_task_template_id?: string;
  task_season?: string;
  generate_report?: boolean;
  service_team_id?: string;
  bank_disconnected_since_date?: string;
  launchedTaskFilter?: string | null;
  is_starred?: boolean;
  has_multiple_us_entities?: boolean;
  is_pre_incorporate?: boolean;
  request_type?: GroupInvitationRequestType | null;
  crm_filters?: string | null;
};

export type PersonalTaxUserInvitePayload = {
  first_name: string;
  last_name: string;
  email: string;
  parent_group_id: string;
};

export type PersonalTaxUserInviteResponse = {
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  profile_id: string;
  group_id: string;
  group_name: string;
  parent_group_id: string;
  status: string;
};

type UpdateCompanyGroupPayload = {
  group_ids: string[];
  update_key: "ops_stage" | "tags" | "sales_person" | "agent";
  update_value: string;
};

export type HandoverDetailScalarValues = {
  customer_name: string;
  closed_by: string;
  cs_assignee: string;
  handover_date: string;
  founder_name: string;
  founder_email: string;
  founder_phone: string;
  founder_residency: string;
  lead_source: string;
  quote_link: string;
  fireflies_link: string;
  incorporated_state: string;
  incorporated_date: string;
  us_activity: string;
  employees: string;
  bookkeeping_method: string;
  shareholding_pattern: string;
  extension_filed: string;
  watch_outs: string;
  confirm_on_call: string;
  attached_document_id: string;
};

export type HandoverAttachmentUploadResponse = {
  uuid: string;
  name: string;
  file_type: keyof typeof FILE_TYPE;
  is_previewable: boolean;
};

// Newline-delimited lists as the wire (API request/response) represents them -- the form layer (useHandoverDetailsFormData/HandoverDetailsModal) splits/joins these into arrays.
export type HandoverDetailWireListValues = {
  states_of_operation: string;
  docs_in_hand: string;
  services_sold: string;
  // Newline-delimited list of states (from states_of_operation) answered "yes" for foreign qualification.
  foreign_qualification: string;
};

export type HandoverDetailListValues = {
  states_of_operation: string[];
  docs_in_hand: string[];
  services_sold: string[];
};

// One Yes/No answer per state currently selected in states_of_operation.
export type HandoverDetailForeignQualificationValues = {
  foreign_qualification: Record<string, string>;
};

// founder_phone_country is populated by MobileInput's country picker and never sent to the backend -- see buildPayload in HandoverDetailsModal.tsx.
export type HandoverDetailFormOnlyValues = {
  founder_phone_country: string;
};

// Shape the form works with -- multi-select fields are arrays and foreign_qualification is a per-state map here, but all round-trip through the API as delimited strings (see HandoverDetailWireListValues).
export type HandoverDetailFormValues = HandoverDetailScalarValues &
  HandoverDetailListValues &
  HandoverDetailForeignQualificationValues &
  HandoverDetailFormOnlyValues;

export type HandoverDetailResponse = HandoverDetailScalarValues &
  HandoverDetailWireListValues & {
    uuid: string;
    submitted_by_name: string | null;
    submitted_at: string | null;
  };

export type HandoverDetailPrefillResponse = Partial<
  HandoverDetailScalarValues & HandoverDetailWireListValues
>;

export type HandoverDetailPayload = HandoverDetailScalarValues &
  HandoverDetailWireListValues;

export type ChurnDetailScalarValues = {
  customer_name: string;
  joining_date: string;
  churn_reason: string;
  save_attempt_performed: string;
  escalations_involved: string;
  save_probability: string;
  csm_score: string;
  other_notes: string;
  company_dissolved: string;
  total_revenue_contribution: string;
  considered_churn: string;
};

export type ChurnDetailListValues = {
  spoc_name: string[];
  products_subscribed: string[];
  filings_status: string[];
};

// Shape the form works with -- the three multi-select fields are arrays here,
// but round-trip through the API as a single delimited string (see ChurnDetailPayload).
export type ChurnDetailFormValues = ChurnDetailScalarValues &
  ChurnDetailListValues;

export type ChurnDetailResponse = ChurnDetailFormValues & {
  uuid: string;
  status: string;
  request_spoc_to_fill: boolean;
  spoc_agent_profile_id: string | null;
};

export type ChurnDetailPrefillResponse = Partial<ChurnDetailFormValues> & {
  spoc_agent_profile_id?: string | null;
};

export type ChurnDetailPayload = ChurnDetailScalarValues & {
  spoc_name: string;
  products_subscribed: string;
  filings_status: string;
  request_spoc_to_fill: boolean;
  draft_uuid?: string;
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllSalesCRMGroups: build.query<
      CompanyGroupResponse,
      GetAllSalesGroupsProps
    >({
      query: ({
        pageNumber,
        searchTerm,
        viewFilter,
        subscriptionFilter,
        agentFilter,
        salespersonFilter,
        usStateFilter,
        incorporationMonthsFilter,
        selectedTag,
        sortDueBy,
        taskState,
        notLaunchedTaskFilter,
        subscriberTypeFilter,
        base_task_template_id,
        task_season,
        generate_report,
        archived,
        service_team_id,
        bank_disconnected_since_date,
        launchedTaskFilter,
        is_starred,
        has_multiple_us_entities,
        is_pre_incorporate,
        request_type,
        crm_filters,
      }) => {
        let queryUrl = qs.stringify(
          {
            page_num: pageNumber,
            search_term: searchTerm,
            crm_type: viewFilter,
            subscription_type: subscriptionFilter,
            agent_filter: agentFilter,
            sales_person_filter: salespersonFilter,
            us_state_uuids: usStateFilter,
            us_incorp_months: incorporationMonthsFilter,
            tag_filter: selectedTag,
            task_state_filter_uuids: taskState,
            not_launched_task_filter: notLaunchedTaskFilter,
            active_subscribers: subscriberTypeFilter,
            base_task_template_id,
            task_season,
            sort_order: sortDueBy,
            sort_by: sortDueBy ? "due_amount" : null,
            generate_report,
            archived,
            service_team_id,
            bank_disconnected_since_date,
            launched_task_filter: launchedTaskFilter,
            is_starred,
            has_multiple_us_entities,
            is_pre_incorporate,
            request_type,
            crm_filters,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `api/inkle/company/v2/get_all_groups/${queryUrl}`,
        };
      },
      providesTags: (result) => {
        const { company_groups = [] } = result || {};
        return result
          ? [
              ...company_groups.map(({ uuid }) => ({
                type: "SalesCrmCompanyGroups" as "SalesCrmCompanyGroups",
                id: uuid,
              })),
              { type: "SalesCrmCompanyGroups", id: "LIST" },
              "NOTES",
            ]
          : [
              {
                type: "SalesCrmCompanyGroups" as "SalesCrmCompanyGroups",
                id: "LIST",
              },
            ];
      },
    }),

    createCompanyAndEntity: build.mutation({
      query: (groupEntityPayload) => {
        return {
          url: "api/inkle/company/group/entities/crm/",
          method: "post",
          body: makeFormData({
            ...groupEntityPayload,
            entity_data: JSON.stringify(groupEntityPayload.entity_data),
            user_data: JSON.stringify(groupEntityPayload.user_data),
          }),
        };
      },
      invalidatesTags: (result, _error) => {
        return result
          ? [
              { type: "SalesCrmCompanyGroups", id: "LIST" },
              { type: "SalesCrmProfiles", id: "LIST" },
            ]
          : [];
      },
    }),

    checkValidEmailMobile: build.mutation<
      {
        mobiles_already_exits: string[];
        emails_already_exits: string[];
      },
      { emails: string[] }
    >({
      query: (payload) => {
        return {
          url: `/api/inkle/users/check-valid-email-mobile/`,
          method: "post",
          body: payload,
        };
      },
    }),

    invitePersonalTaxUser: build.mutation<
      PersonalTaxUserInviteResponse,
      PersonalTaxUserInvitePayload
    >({
      query: (payload) => ({
        url: `api/inkle/company/v2/personal_tax_user/invite/`,
        method: "post",
        body: payload,
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: "SalesCrmCompanyGroups", id: "LIST" }],
    }),

    updateGroupData: build.mutation({
      query: ({ groupId, payLoad }) => {
        return {
          url: `api/inkle/company/v2/group/${groupId}/update_crm_group/`,
          method: "PATCH",
          body: payLoad,
        };
      },
      invalidatesTags: (result, _error) => {
        return result
          ? [
              { type: "SalesCrmCompanyGroups", id: "LIST" },
              {
                type: "SliderCRM",
                id: "LIST",
              },
              { type: "BOOKS_CRM" },
              { type: "BOOKKEEPING_ENTITY" },
            ]
          : [];
      },
    }),

    getHandoverDetailPrefill: build.query<
      HandoverDetailPrefillResponse,
      { groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `api/inkle/company/v2/group/${groupId}/handover_details/prefill/`,
      }),
    }),

    getLatestHandoverDetail: build.query<
      HandoverDetailResponse | null,
      { groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `api/inkle/company/v2/group/${groupId}/handover_details/latest/`,
      }),
      providesTags: (result, error, { groupId }) => [
        { type: "HANDOVER_DETAIL", id: `LATEST-${groupId}` },
      ],
    }),

    submitHandoverDetail: build.mutation<
      HandoverDetailResponse,
      { groupId: string; payload: HandoverDetailPayload }
    >({
      query: ({ groupId, payload }) => ({
        url: `api/inkle/company/v2/group/${groupId}/handover_details/`,
        method: "post",
        body: payload,
      }),
      invalidatesTags: (result, error, { groupId }) => {
        return result
          ? [
              { type: "SalesCrmCompanyGroups", id: "LIST" },
              { type: "SliderCRM", id: "LIST" },
              { type: "HANDOVER_DETAIL", id: `LATEST-${groupId}` },
            ]
          : [];
      },
    }),

    uploadHandoverAttachment: build.mutation<
      HandoverAttachmentUploadResponse,
      { groupId: string; file: File }
    >({
      query: ({ groupId, file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `api/inkle/company/v2/group/${groupId}/handover_details/attachment/`,
          method: "post",
          body: formData,
        };
      },
    }),

    getChurnDetailPrefill: build.query<
      ChurnDetailPrefillResponse,
      { groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `api/inkle/company/v2/group/${groupId}/churn_details/prefill/`,
      }),
    }),

    getChurnDetailDraft: build.query<
      ChurnDetailResponse,
      { groupId: string; draftId: string }
    >({
      query: ({ groupId, draftId }) => ({
        url: `api/inkle/company/v2/group/${groupId}/churn_details/${draftId}/`,
      }),
      providesTags: (result, error, { draftId }) => [
        { type: "CHURN_DETAIL", id: draftId },
      ],
    }),

    getLatestChurnDetail: build.query<
      ChurnDetailResponse | null,
      { groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `api/inkle/company/v2/group/${groupId}/churn_details/latest/`,
      }),
      providesTags: (result, error, { groupId }) => [
        { type: "CHURN_DETAIL", id: `LATEST-${groupId}` },
      ],
    }),

    submitChurnDetail: build.mutation<
      ChurnDetailResponse,
      { groupId: string; payload: ChurnDetailPayload }
    >({
      query: ({ groupId, payload }) => ({
        url: `api/inkle/company/v2/group/${groupId}/churn_details/`,
        method: "post",
        body: payload,
      }),
      invalidatesTags: (result, error, { groupId }) => {
        return result
          ? [
              { type: "SalesCrmCompanyGroups", id: "LIST" },
              { type: "SliderCRM", id: "LIST" },
              { type: "CHURN_DETAIL", id: result.uuid },
              { type: "CHURN_DETAIL", id: `LATEST-${groupId}` },
            ]
          : [];
      },
    }),

    updateUserData: build.mutation<
      any,
      { profileId: string; payload: { [key: string]: string | boolean } }
    >({
      query: ({ profileId, payload }) => {
        return {
          url: `api/inkle/users/profile/${profileId}/update/`,
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: [{ type: "SalesCrmProfiles" }],
    }),

    getAllEmails: build.query({
      query: ({ searchTerm, pageNumber }) => {
        let queryUrl = qs.stringify(
          {
            page_num: pageNumber,
            search_term: searchTerm,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `api/inkle/company/v2/get_all_emails/${queryUrl}`,
          method: "get",
        };
      },
    }),

    getAllCoupons: build.query<CouponResponse[], void>({
      query: () => {
        return {
          url: "/api/inkle/users/get_all_coupons/",
        };
      },
    }),

    getSliderData: build.query<
      SliderCRM,
      { groupId: string; base_task_template_id?: string }
    >({
      query: ({ groupId, base_task_template_id }) => {
        let queryUrl = qs.stringify(
          {
            base_task_template_id,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/company/v2/group/${groupId}/slider/${queryUrl}`,
        };
      },
      providesTags: [
        {
          type: "SliderCRM" as "SliderCRM",
          id: "LIST",
        },
      ],
    }),
    updateBulkCompanyGroup: build.mutation<
      void,
      { payload: UpdateCompanyGroupPayload }
    >({
      query: ({ payload }) => {
        return {
          url: `/api/inkle/company/groups/bulk_update/`,
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: [
        "SalesCrmCompanyGroups",
        "SliderCRM",
        "BOOKS_CRM",
        "BOOKKEEPING_ENTITY",
      ],
    }),
  }),

  overrideExisting: false,
});

export const {
  useCreateCompanyAndEntityMutation,
  useGetAllSalesCRMGroupsQuery,
  useLazyGetAllSalesCRMGroupsQuery,
  useCheckValidEmailMobileMutation,
  useInvitePersonalTaxUserMutation,
  useUpdateGroupDataMutation,
  useUpdateUserDataMutation,
  useGetAllEmailsQuery,
  useGetAllCouponsQuery,
  useGetSliderDataQuery,
  useUpdateBulkCompanyGroupMutation,
  useGetHandoverDetailPrefillQuery,
  useGetLatestHandoverDetailQuery,
  useSubmitHandoverDetailMutation,
  useUploadHandoverAttachmentMutation,
  useGetChurnDetailPrefillQuery,
  useGetChurnDetailDraftQuery,
  useGetLatestChurnDetailQuery,
  useLazyGetLatestChurnDetailQuery,
  useSubmitChurnDetailMutation,
} = extendedApi;
